/**
 * E018-S013: FullscreenDatabaseRootBlock
 *
 * A minimal root block that renders ONLY the selected database.
 * Reads `data-database-id` from the parent container to know which one.
 *
 * sl-3nk: Added MutationObserver to react to external attribute changes
 * (fixes tab-switch not showing the new database)
 */

import { I18n } from '@affine/i18n';
import type { RootBlockModel } from '@blocksuite/affine/model';
import { BlockComponent } from '@blocksuite/affine/std';
import { html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

function closestAcrossShadowRoots(
  start: Element,
  selector: string
): Element | null {
  let current: Element | null = start;
  while (current) {
    if (current.matches(selector)) {
      return current;
    }
    if (current.parentElement) {
      current = current.parentElement;
      continue;
    }

    const root = current.getRootNode();
    current = root instanceof ShadowRoot ? root.host : null;
  }
  return null;
}

export class FullscreenDatabaseRootBlock extends BlockComponent<RootBlockModel> {
  private _observer: MutationObserver | null = null;
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;
  private _retryCount = 0;
  private static readonly MAX_RETRIES = 20; // 20 * 200ms = 4s max wait

  // sl-3nk: Track the target database ID as reactive state
  @state()
  private accessor _targetDatabaseId: string | null = null;

  override connectedCallback() {
    super.connectedCallback();

    // sl-3nk: Initial read of target database ID
    this._updateTargetDatabaseId();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._observer?.disconnect();
    this._observer = null;
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  private _updateTargetDatabaseId() {
    const container = closestAcrossShadowRoots(this, '[data-database-id]');
    if (container && !this._observer) {
      this._observer = new MutationObserver(() => {
        this._updateTargetDatabaseId();
      });
      this._observer.observe(container, {
        attributes: true,
        attributeFilter: ['data-database-id'],
      });
    }

    const newId = container?.getAttribute('data-database-id') ?? null;
    if (newId !== this._targetDatabaseId) {
      this._targetDatabaseId = newId;
    }
  }

  // sl-a7z: Switch to edgeless mode to create a database
  private readonly _createDatabaseAndOpen = () => {
    // Close fullscreen and switch to edgeless mode
    // User can then use the toolbar (+) to add a Database
    window.dispatchEvent(new CustomEvent('affine:switch-to-edgeless-mode'));
  };

  override renderBlock() {
    // sl-3nk: Use reactive state instead of reading attribute directly
    const targetDatabaseId = this._targetDatabaseId;

    // sl-a7z: Show placeholder when no database exists
    if (targetDatabaseId === '__placeholder__') {
      return html`
        <div class="fullscreen-database-placeholder">
          <div class="placeholder-content">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
            <h2>${I18n.t('com.streamlain.databaseFullscreen.empty.title')}</h2>
            <p>
              ${I18n.t('com.streamlain.databaseFullscreen.empty.canvasHint')}
            </p>
            <button
              class="create-database-btn"
              @click=${this._createDatabaseAndOpen}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              ${I18n.t('com.streamlain.databaseFullscreen.empty.goToCanvas')}
            </button>
            <p class="sub-hint">
              ${I18n.t('com.streamlain.databaseFullscreen.empty.toolbarHint')}
            </p>
            <p class="hint">
              ${I18n.t(
                'com.streamlain.databaseFullscreen.empty.returnHintPrefix'
              )}
              <kbd>Alt</kbd>+<kbd>E</kbd>
              ${I18n.t(
                'com.streamlain.databaseFullscreen.empty.returnHintSuffix'
              )}
            </p>
          </div>
        </div>
        <style>
          .fullscreen-database-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            min-height: 400px;
            color: var(--streamlain-text-secondary-color);
          }
          .placeholder-content {
            text-align: center;
            max-width: 400px;
          }
          .placeholder-content svg {
            opacity: 0.5;
            margin-bottom: 16px;
          }
          .placeholder-content h2 {
            margin: 0 0 8px 0;
            font-size: 20px;
            font-weight: 500;
            color: var(--streamlain-text-primary-color);
          }
          .placeholder-content p {
            margin: 0 0 8px 0;
            font-size: 14px;
          }
          .placeholder-content .create-database-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin: 16px 0;
            padding: 10px 20px;
            background: var(--streamlain-primary-color);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
          }
          .placeholder-content .create-database-btn:hover {
            background: var(--streamlain-primary-color-hover, #1e6fff);
          }
          .placeholder-content .create-database-btn svg {
            opacity: 1;
            margin: 0;
          }
          .placeholder-content .sub-hint {
            margin: 0 0 8px 0;
            font-size: 12px;
            opacity: 0.6;
          }
          .placeholder-content .hint {
            margin-top: 16px;
            font-size: 12px;
            opacity: 0.7;
          }
          .placeholder-content kbd {
            background: var(--streamlain-hover-color);
            border-radius: 4px;
            padding: 2px 6px;
            font-family: inherit;
            font-size: 11px;
          }
        </style>
      `;
    }

    // Render only the target database.
    // The database may be nested inside a Note (root → note → database),
    // so we resolve its parent via O(1) store lookup instead of filtering
    // direct children of root (which would miss nested databases).
    let children;

    if (targetDatabaseId) {
      const targetBlock = this.store.getBlock(targetDatabaseId);
      if (targetBlock?.model.parent) {
        // Block found — reset retry counter and render
        this._retryCount = 0;
        children = this.renderChildren(targetBlock.model.parent, child => {
          return (
            child.flavour === 'affine:database' && child.id === targetDatabaseId
          );
        });
      } else if (this._retryCount < FullscreenDatabaseRootBlock.MAX_RETRIES) {
        // Block not yet in store (Y.js doc still loading on cold start).
        // Schedule a re-render after a short delay.
        this._retryCount++;
        this._retryTimer = setTimeout(() => {
          this._retryTimer = null;
          this.requestUpdate();
        }, 200);
      }
    } else if (this._retryCount < FullscreenDatabaseRootBlock.MAX_RETRIES) {
      // Target id can be unavailable during initial shadow-DOM attachment.
      // Never fall back to rendering every database in fullscreen.
      this._retryCount++;
      this._retryTimer = setTimeout(() => {
        this._retryTimer = null;
        this._updateTargetDatabaseId();
        this.requestUpdate();
      }, 200);
    }

    if (!children || (Array.isArray(children) && children.length === 0)) {
      return nothing;
    }

    // sl-gka: Render widgets (including RemoteSelectionWidget for presence)
    const widgets = html`${repeat(
      Object.entries(this.widgets),
      ([id]) => id,
      ([_, widget]) => widget
    )}`;

    return html`<div class="fullscreen-database-root">
      ${children} ${widgets}
    </div>`;
  }
}
